import { useEffect, useId, useRef, useState } from "react";
import {
  addCatalogItinerary,
  getItineraryCatalog,
} from "../../../../services/itinerary";
import type {
  ItineraryCatalogEntry,
  ItineraryRequest,
  PagedItineraryCatalog,
} from "../../../../types/itinerary";
import { getErrorMessage } from "../../../../utils/errorHandler";
import { formatEventDateRange } from "./eventDateRange";
import { ItineraryRequestForm } from "./ItineraryRequestForm";
import { ItineraryRequestConfirmation } from "./ItineraryRequestConfirmation";
import { Button } from "../../../../components/Button";
import { TextInput } from "../../../../components/TextInput";
import { ModalCloseButton } from "../../../../components/ModalCloseButton";
import { useOverlayDialog } from "./useOverlayDialog";
import { tourAnchor } from "../../../tours/tourAnchors";
import { useTour } from "../../../tours/useTour";
import "./Itinerary.css";

/** Entries per page. Enough to fill the panel without scrolling forever. */
const PAGE_SIZE = 8;

/** The catalog's page tour. The schedule page runs `schedule.page` / `schedule.data`. */
const CATALOG_TOUR_KEY = "schedule.catalog";

interface ItineraryCatalogPanelProps {
  onClose: () => void;
  /** An itinerary landed on the org's calendar — the caller refetches the visible range. */
  onAdded?: (calendarEventId: string) => void;
}

/**
 * Browse the shared itinerary catalog and add a tournament to your own calendar — and, when nothing
 * matches, ask RosterPilot to build one instead.
 *
 * <b>It opens as a list, not as a search box.</b> The catalog leads with what RosterPilot has most
 * recently published and pages back through it; searching by name is there for a coordinator who
 * already knows what they want. The previous shape required a date window, which meant opening the
 * catalog showed nothing at all until the coach guessed one.
 *
 * Browse and request deliberately live on ONE surface. Splitting them puts the request behind a
 * separate menu the coach only finds if they already know it exists, when the moment they need it
 * is precisely the moment this list came back empty.
 */
export const ItineraryCatalogPanel = ({ onClose, onAdded }: ItineraryCatalogPanelProps) => {
  const fieldId = useId();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  /**
   * The page as the SERVER described it, kept as one object rather than as separate `entries` /
   * `totalCount` / `totalPages` values.
   *
   * That is what stops `page > totalPages` being representable. With them apart, a coordinator
   * sitting on page 4 when another org unpublishes enough tournaments to drop the catalog to two
   * pages got `items: []` with `totalPages: 2` — so the panel rendered "RosterPilot has not
   * published any itineraries yet" over a catalog holding a dozen tournaments, under a pager
   * reading "Page 4 of 2". Sourcing all four from one response makes that shape impossible.
   */
  const [result, setResult] = useState<PagedItineraryCatalog>({
    items: [],
    page: 1,
    pageSize: PAGE_SIZE,
    totalCount: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Per-row pending state, and a SET rather than one id: a single slot re-enables row A's button
  // while A's request is still in flight the moment B is tapped, so the buttons flicker in an
  // order unrelated to what the coach did.
  const [addingIds, setAddingIds] = useState<ReadonlySet<string>>(new Set());
  const [addError, setAddError] = useState("");
  const [mode, setMode] = useState<"browse" | "request">("browse");
  const [submitted, setSubmitted] = useState<ItineraryRequest | null>(null);

  const { items: entries, totalCount, totalPages } = result;

  // Monotonic token guarding out-of-order responses: typing in the search box fires overlapping
  // reads, and a slow earlier one must not overwrite the fresher list.
  const fetchTokenRef = useRef(0);
  // Only a SEARCH needs coalescing. The first load and a pager click are single deliberate
  // actions, and making them wait 300ms before the request even starts is latency for nothing.
  const debounceRef = useRef(false);

  // Bumped to force a refetch. Needed by "Try again", which asks for the page already in state —
  // without it, setting `page` to its current value changes nothing and the effect never re-runs.
  const [reloadNonce, setReloadNonce] = useState(0);

  const goToPage = (next: number) => {
    debounceRef.current = false;
    setAddError("");
    setPage(next);
    setReloadNonce((n) => n + 1);
  };

  useEffect(() => {
    const token = ++fetchTokenRef.current;
    const controller = new AbortController();
    setLoading(true);

    const run = () => {
      getItineraryCatalog({ page, pageSize: PAGE_SIZE, search }, controller.signal)
        .then((found) => {
          if (token !== fetchTokenRef.current) return;
          // The server's own page number, not ours: it clamps and echoes, and trusting our
          // optimistic value is how the pager and the list get to disagree.
          setResult(found);
          setPage(found.page);
          setError("");
          setLoading(false);
        })
        .catch((err) => {
          // An abort rejects, so a superseded or unmounted read would otherwise surface as
          // "signal is aborted without reason" in the coach's error banner.
          if (token !== fetchTokenRef.current || controller.signal.aborted) return;
          setError(getErrorMessage(err, "Couldn't load the itinerary catalog. Try again."));
          setResult((prev) => ({ ...prev, items: [], totalCount: 0, totalPages: 1 }));
          setLoading(false);
        });
    };

    if (!debounceRef.current) {
      debounceRef.current = true;
      run();
      return () => {
        // Bumped so the in-flight read's handlers bail even on unmount, where nothing else
        // supersedes the token.
        fetchTokenRef.current++;
        controller.abort();
      };
    }

    const handle = setTimeout(run, 300);
    return () => {
      clearTimeout(handle);
      fetchTokenRef.current++;
      controller.abort();
    };
  }, [page, search, reloadNonce]);

  const handleAdd = async (entry: ItineraryCatalogEntry) => {
    setAddingIds((prev) => new Set(prev).add(entry.id));
    setAddError("");
    try {
      const added = await addCatalogItinerary(entry.id);
      setResult((prev) => ({
        ...prev,
        items: prev.items.map((e) =>
          e.id === entry.id
            ? { ...e, alreadyOnMyCalendar: true, myCalendarEventId: added.calendarEventId }
            : e,
        ),
      }));
      onAdded?.(added.calendarEventId);
    } catch (err) {
      setAddError(getErrorMessage(err, `Couldn't add ${entry.name}. Try again.`));
    } finally {
      setAddingIds((prev) => {
        const next = new Set(prev);
        next.delete(entry.id);
        return next;
      });
    }
  };

  const dialogRef = useOverlayDialog(onClose);

  const nothingFound = !loading && !error && entries.length === 0;

  // The catalog is its own view of the schedule, with its own page tour and no data tour. Its
  // anchors only exist while browsing, so the request form and its confirmation hold the tour.
  useTour("schedule", {
    ready: !loading && mode === "browse" && !submitted,
    hasData: entries.length > 0,
    pageTourKey: CATALOG_TOUR_KEY,
    dataTourKey: null,
  });
  // "Add" is the step's subject, and a tournament already on the calendar shows no Add button.
  const firstAddableId = entries.find((e) => !e.alreadyOnMyCalendar)?.id;

  return (
    <aside
      ref={dialogRef as React.RefObject<HTMLElement>}
      className="event-editor itin-catalog"
      // The overlay behind this panel is a full-screen dim, so it IS a modal dialog and has to say
      // so: without these, focus tabbed straight through into the calendar behind the backdrop and
      // a screen reader read the page as though the panel were not open. Escape and the focus trap
      // come from useOverlayDialog.
      role="dialog"
      aria-modal="true"
      tabIndex={-1}
      aria-label="Find a tournament itinerary"
    >
      <div className="event-editor__head">
        <div className="event-editor__title">Find a tournament itinerary</div>
        <div className="itin-catalog__head-actions">
          <ModalCloseButton onClick={onClose} />
        </div>
      </div>

      <div className="event-editor__body">
        {submitted ? (
          <ItineraryRequestConfirmation
            request={submitted}
            doneLabel="Back to the catalog"
            onDone={() => {
              setSubmitted(null);
              setMode("browse");
            }}
          />
        ) : mode === "request" ? (
          <ItineraryRequestForm
            defaultName={search.trim()}
            onCancel={() => setMode("browse")}
            onSubmitted={setSubmitted}
          />
        ) : (
          <>
            {/* Search is the ONLY filter, and it is optional: the list below already shows the
                most recently published tournaments, so a coordinator who does not know what they
                are looking for still sees something. */}
            <TextInput
              className="itin-field"
              label={
                <>
                  Search by name{" "}
                  <span className="itin-field__optional">(optional)</span>
                </>
              }
              id={`${fieldId}-search`}
              type="search"
              value={search}
              onChange={(e) => {
                // Page reset happens HERE, in the same commit as the term, rather than in a
                // second effect keyed on `search`. Two effects reacting to one value only avoided
                // a stale page-3 request because the debounce outlasted the re-render — true
                // today, and not a thing to rely on.
                debounceRef.current = true;
                setSearch(e.target.value);
                setPage(1);
                setAddError("");
              }}
              placeholder="Search published itineraries"
              data-testid="itinerary-catalog-search"
              {...tourAnchor("schedule.catalog.search")}
            />

            {error && (
              <div className="event-editor__error" role="alert" data-testid="itinerary-catalog-error">
                {error}{" "}
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => goToPage(page)}
                  data-testid="itinerary-catalog-retry"
                >
                  Try again
                </Button>
              </div>
            )}
            {addError && (
              <div className="event-editor__error" role="alert">
                {addError}
              </div>
            )}

            {loading ? (
              <p className="event-editor__hint" data-testid="itinerary-catalog-loading">
                Loading itineraries…
              </p>
            ) : entries.length > 0 ? (
              <ul
                className="itin-catalog__list"
                data-testid="itinerary-catalog-list"
                {...tourAnchor("schedule.catalog.list")}
              >
                {entries.map((entry) => (
                  <li key={entry.id} className="itin-catalog__item">
                    <div className="itin-catalog__info">
                      <span className="itin-catalog__name">{entry.name}</span>
                      <span className="itin-catalog__dates">{formatEventDateRange(entry)}</span>
                      {(entry.locationName || entry.locationAddress) && (
                        <span className="itin-catalog__loc">
                          📍 {entry.locationName ?? entry.locationAddress}
                        </span>
                      )}
                      <span className="itin-catalog__games">
                        {entry.gameCount === 1 ? "1 game" : `${entry.gameCount} games`}
                      </span>
                    </div>
                    {entry.alreadyOnMyCalendar ? (
                      <span
                        className="itin-catalog__on"
                        data-testid={`itinerary-catalog-on-${entry.id}`}
                      >
                        On your calendar
                      </span>
                    ) : (
                      <Button
                        type="button"
                        variant="primary"
                        onClick={() => void handleAdd(entry)}
                        disabled={addingIds.has(entry.id)}
                        aria-label={`Add ${entry.name} to my calendar`}
                        data-testid={`itinerary-catalog-add-${entry.id}`}
                        {...(entry.id === firstAddableId
                          ? tourAnchor("schedule.catalog.row.add")
                          : {})}
                      >
                        {addingIds.has(entry.id) ? "Adding…" : "Add to my calendar"}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            ) : nothingFound ? (
              <p
                className="event-editor__hint"
                data-testid="itinerary-catalog-empty"
                {...tourAnchor("schedule.catalog.list")}
              >
                {search.trim()
                  ? "No published itineraries match that name."
                  : "RosterPilot has not published any itineraries yet."}
              </p>
            ) : null}

            {/*
                Kept mounted regardless of page count, and it is a `status` region: paging and
                searching both swap the list in place, so without an announcement on ARRIVAL a
                screen reader user gets no confirmation that anything happened. It announces the
                loaded result, never the pending one — a region that fires "Page 2 of 5" while the
                list still says "Loading" describes a screen that does not exist yet.
            */}
            <p
              className="itin-catalog__pager-status"
              role="status"
              aria-live="polite"
              data-testid="itinerary-catalog-page-status"
            >
              {loading
                ? "Loading itineraries…"
                : totalCount === 0
                  ? ""
                  : totalPages > 1
                    ? `Page ${page} of ${totalPages}, showing ${entries.length} of ${totalCount} tournaments`
                    : `${totalCount} ${totalCount === 1 ? "tournament" : "tournaments"}`}
            </p>

            {totalPages > 1 && (
              <nav
                className="itin-catalog__pager"
                aria-label="Catalog pages"
                data-testid="itinerary-catalog-pager"
              >
                {/*
                    Deliberately NOT `disabled` while loading. Disabling the button a keyboard user
                    has just activated makes the browser drop focus to <body> — and inside this
                    focus-trapped dialog the next Tab restarts from the top. The handlers no-op
                    instead, so the control keeps focus and the page it lands on is announced above.
                */}
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    if (!loading && page > 1) goToPage(page - 1);
                  }}
                  aria-disabled={page <= 1 || loading}
                  data-testid="itinerary-catalog-prev"
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    if (!loading && page < totalPages) goToPage(page + 1);
                  }}
                  aria-disabled={page >= totalPages || loading}
                  data-testid="itinerary-catalog-next"
                >
                  Next
                </Button>
              </nav>
            )}

            <div className="itin-catalog__ask">
              <p className="itin-catalog__ask-msg">
                {nothingFound
                  ? "Can't find the tournament you're going to? We'll build the itinerary for you."
                  : "Not the tournament you're going to? We'll build that itinerary for you."}
              </p>
              <Button
                type="button"
                variant="primary"
                onClick={() => setMode("request")}
                data-testid="itinerary-catalog-request-cta"
                {...tourAnchor("schedule.catalog.request")}
              >
                Request an itinerary
              </Button>
            </div>
          </>
        )}
      </div>

      {/*
          No footer. It held a lone "Close" button duplicating the X in the header, and each mode
          of this panel already carries its own way out: the request form has Cancel, the
          confirmation has Done. A second dismissal with no other action beside it is just a
          second thing to read. Panels whose footer has a real primary action next to Close keep
          theirs (see ItineraryPanel).
      */}
    </aside>
  );
};
